import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";

import { BranchesService } from "../../../services/branches-service";
import { IBranchResponse } from "../../vue-interfaces/i-branch";
import { EnumMap } from "../../../common/enum-map";
import { ApplicationEnum, ParentPermissionEnum, PermissionActionEnum } from "../../../enums/enums";
import { ProvidersService } from "../../../services/providers-service";
import { PermissionManager } from "../../../common/utilities/permission-manager";

import GroupedMultiSelect from "../GroupedMultiSelect/GroupedMultiSelect.vue";

export default defineComponent({
    components: {
        GroupedMultiSelect
    },
    props: {
        selectedBranches: {
            type: Array,
            default: []
        },
        checkAllByDefault: {
            type: Boolean,
            default: false
        },
        notAllowedSolutions: {
            type: Array,
            default: null
        },
        selectedAgenciesName: {
            type: Array,
            default: []
        },
        isInquiry: {
            type: Boolean,
            default: false
        },
        isReferral: {
            type: Boolean,
            default: false
        },
        isCalendar: {
            type: Boolean,
            default: false
        },
        showPrimaryBranch: {
            type: Boolean,
            default: false
        },
        isPrimaryLocationFilter: {
            type: Boolean,
            default: false
        },
        preserveSelectedBranches: {
            type: Boolean,
            default: false
        },
        classes: { type: String, default: null },
        isBranchSynced: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            _providersService: null,
            _branchservice: null,
            _permissionManager: null,
            dropDownOptions: null,
            branches: [],
            providerIds: [],
            filterBranches: [],
            branchEnumMap: null,
            labelText: "Branches",
            searchText: "Branches",
            isBranchLoading: false,
            providers: [],
            isRequestPending: false,
            cacheBranches: []
        };
    },
    emits: ["updateBranches", "update:isBranchSynced", "updatePrimaryLocationFilter"],
    created() {
        this._branchservice = Container.instance.get(BranchesService);
        this._providersService = Container.instance.get(ProvidersService);
        this._permissionManager = Container.instance.get(PermissionManager);
    },

    mounted() {
        this.initFilters();
    },

    watch: {
        selectedBranches: {
            handler(newValue: string[]) {
                const selectedBranchIds = newValue.filter((branchId) =>
                    this.branches.some((branch) => branch.id === branchId)
                );
                const isSelectionUnchanged =
                    selectedBranchIds.length === this.filterBranches.length &&
                    selectedBranchIds.every((branchId) => this.filterBranches.includes(branchId));
                if (!isSelectionUnchanged) {
                    this.filterBranches = selectedBranchIds;
                }
            },
            deep: true
        },
        filterBranches: {
            handler() {
                this.filterBranchesChanged();
            },
            deep: true
        },
        async selectedAgenciesName(newValue: any) {
            this.branches = [...this.cacheBranches];
            this.branches = this.branches?.filter((branch) => this.selectedAgenciesName?.includes(branch.providerName));
            this.refreshBranchOptions();
            this.filterBranches = this.preserveSelectedBranches
                ? this.filterBranches.filter((branchId) => this.branches.some((branch) => branch.id === branchId))
                : this.branches.map((branch) => branch.id);
        },
        isPrimaryLocationFilter() {
            this.branches = [...this.cacheBranches];
            if (this.selectedAgenciesName.length) {
                this.branches = this.branches.filter((branch) =>
                    this.selectedAgenciesName.includes(branch.providerName)
                );
            }
            this.refreshBranchOptions();
            this.filterBranches = this.filterBranches.filter((branchId) =>
                this.branches.some((branch) => branch.id === branchId)
            );
        }
    },

    methods: {
        async initFilters() {
            this.branches = await this._branchservice.getAllBranches();
            let schedulerViewPermissions;
            if (this.isInquiry) {
                schedulerViewPermissions = await this._permissionManager.getPermmission(
                    ParentPermissionEnum.inquiries,
                    PermissionActionEnum.canView
                );
            } else if (this.isReferral) {
                schedulerViewPermissions = await this._permissionManager.getPermmission(
                    ParentPermissionEnum.referrals,
                    PermissionActionEnum.canView
                );
            } else if (this.isCalendar) {
                schedulerViewPermissions = await this._permissionManager.getPermmission(
                    ParentPermissionEnum.scheduler,
                    PermissionActionEnum.canView
                );
            } else {
                schedulerViewPermissions = await this._permissionManager.getPermmission(
                    ParentPermissionEnum.clinician,
                    PermissionActionEnum.canView
                );
            }

            const allowedProducts = [
                ApplicationEnum.AgencyCore,
                ApplicationEnum.HomeCare,
                ApplicationEnum.AxxessHospice,
                ApplicationEnum.AxxessHospiceFE,
                ApplicationEnum.AxxessPalliative
            ];

            const schedulerAgencies = schedulerViewPermissions
                .filter((item: any) => {
                    if (!item) {
                        return false;
                    }
                    let application = item.application;
                    if (application === ApplicationEnum.AxxessHospiceFE) {
                        application = ApplicationEnum.AxxessHospice;
                    }
                    return allowedProducts.includes(application);
                })
                .map((x: any) => x.agencyId);

            this.branches = this.branches.filter((branch) => {
                const isAllowed = allowedProducts.includes(branch.application);
                return isAllowed && schedulerAgencies.includes(branch.accountId);
            });

            if (this.notAllowedSolutions?.length) {
                this.branches = this.branches.filter(
                    (branch) => !this.notAllowedSolutions.includes(branch.application)
                );
            }
            this.providers = await this._providersService.getProvider();
            this.providerIds = this.providers.map((provider) => provider.id);
            this.cacheBranches = this.branches;
            if (this.selectedAgenciesName.length) {
                this.branches = this.branches.filter((branch) =>
                    this.selectedAgenciesName.includes(branch.providerName)
                );
            }
            this.refreshBranchOptions();
            if (!this.checkAllByDefault) {
                this.isRequestPending = true;
                let defaultBranches: IBranchResponse[] = this.getBranchDefaults();
                this.filterBranches =
                    this.selectedBranches?.length > 0
                        ? Array.from(this.selectedBranches)
                        : defaultBranches.map((branch) => branch.id);
            } else {
                this.filterBranches = this.branches.map((branch) => branch.id);
            }
        },

        filterBranchesChanged() {
            this.$emit("update:isBranchSynced", true);
            this.$emit("updateBranches", this.filterBranches);
        },

        getAllProviderBranchIds(provider: string) {
            let branchIds: string[] = [];
            this.branches.forEach((branch) => {
                if (branch.providerId === provider.split("_")[0] && branch.application == provider.split("_")[1]) {
                    branchIds.push(branch.id);
                }
            });
            return branchIds;
        },
        getDropDownOptions() {
            let branchOptions: { [key: string]: any } = {};
            let providers = [
                ...new Set(this.branches.map((branch) => `${branch.providerId}_${branch.application}`))
            ] as string[];
            providers.forEach((providerId: string) => {
                branchOptions[providerId] = this.getAllProviderBranchIds(providerId);
            });
            return branchOptions;
        },
        getBranchDefaults() {
            return this.branches.filter((branch) => this.providerIds.includes(branch.providerId));
        },
        getProviderName(provider: string) {
            const agency = this.branches.find((branch) => {
                return branch.providerId === provider.split("_")[0] && branch.application == provider.split("_")[1];
            });
            return agency.providerName;
        },
        getProviderApplication(provider: string) {
            const agency = this.branches.find((branch) => {
                return branch.providerId === provider.split("_")[0] && branch.application == provider.split("_")[1];
            });
            if (agency.application === ApplicationEnum.AxxessHospiceFE) {
                return ApplicationEnum.AxxessHospice;
            }
            return agency.application;
        },
        refreshBranchOptions() {
            if (this.isPrimaryLocationFilter) {
                const primaryHomeHealthAgencyIds = [
                    "ae3934e9-100f-41cd-8c23-c5c176033f12",
                    "397cb4c-de20-48d6-940e-11d4fea48a88"
                ];
                this.branches = this.branches.filter(
                    (branch) =>
                        branch.application !== ApplicationEnum.AgencyCore ||
                        primaryHomeHealthAgencyIds.includes(branch.accountId)
                );
            }
            this.dropDownOptions = this.getDropDownOptions();
            this.branchEnumMap = new EnumMap(this.createBranchEnum());
        },
        createBranchEnum() {
            return this.branches.map((branch) => ({
                name: branch.name,
                value: branch.id
            }));
        },
        updateFilterBranches(branches: any) {
            this.filterBranches.length = 0;
            this.filterBranches.push(...branches);
        },
        updatePrimaryLocationFilter(value: boolean) {
            this.$emit("updatePrimaryLocationFilter", value);
        }
    }
});
